import { Listbox } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

/**
 * InlineSelect Component
 *
 * A compact inline dropdown selector styled as a badge, perfect for
 * inline editing in detail views and sidebars.
 *
 * @example
 * ```jsx
 * // Basic usage
 * <InlineSelect
 *   value={selectedId}
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 *   onChange={setSelectedId}
 * />
 *
 * // With custom display and empty option
 * <InlineSelect
 *   value={categoryId}
 *   options={categories.map(c => ({ value: c.id, label: c.name }))}
 *   onChange={handleCategoryChange}
 *   placeholder="No category"
 *   emptyOption={{ value: '', label: 'No category' }}
 * />
 *
 * // With custom render for selected value
 * <InlineSelect
 *   value={userId}
 *   options={users}
 *   onChange={setUserId}
 *   renderValue={(option) => (
 *     <span className="flex items-center gap-1">
 *       <Avatar id={option.label} size="xxs" />
 *       {option.label}
 *     </span>
 *   )}
 * />
 *
 * // With custom button render (e.g., StatusBadge)
 * <InlineSelect
 *   value={priority}
 *   options={priorityOptions}
 *   onChange={setPriority}
 *   renderButton={(option, displayValue) => (
 *     <StatusBadge preset="priority" value={option?.value}>
 *       {option?.label || displayValue}
 *     </StatusBadge>
 *   )}
 * />
 * ```
 */

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const InlineSelect = ({
	value,
	options = [],
	onChange,
	placeholder = 'Select...',
	emptyOption = null,
	renderValue = null,
	renderButton = null,
	renderOption = null,
	disabled = false,
	className = '',
	buttonClassName = '',
	optionsClassName = '',
	size = 'sm',
	position = 'left',
}) => {
	// Find the currently selected option
	const selectedOption = options.find((opt) => opt.value === value);
	const displayValue = selectedOption?.label || placeholder;

	// Size classes for the button
	const sizeClasses = {
		xs: 'px-1.5 py-0.5 text-xs',
		sm: 'px-2 py-0.5 text-xs',
		md: 'px-2.5 py-1 text-sm',
	};

	// Position classes for the dropdown
	const positionClasses = {
		left: 'left-0',
		right: 'right-0',
	};

	// Combine empty option with regular options
	const allOptions = emptyOption ? [emptyOption, ...options] : options;

	return (
		<Listbox value={value} onChange={onChange} disabled={disabled}>
			<div className={classNames('relative', className)}>
				<Listbox.Button
					className={classNames(
						'flex items-center gap-1',
						disabled && 'opacity-50 cursor-not-allowed',
						buttonClassName,
					)}
				>
					{renderButton ? (
						renderButton(selectedOption, displayValue)
					) : (
						<span
							className={classNames(
								'inline-flex items-center rounded font-medium cursor-pointer',
								'bg-gray-100 text-gray-700 hover:bg-gray-200',
								sizeClasses[size] || sizeClasses.sm,
							)}
						>
							{renderValue && selectedOption
								? renderValue(selectedOption)
								: displayValue}
						</span>
					)}
				</Listbox.Button>

				<Listbox.Options
					className={classNames(
						'absolute z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
						positionClasses[position] || positionClasses.left,
						optionsClassName,
					)}
				>
					{allOptions.map((option) => (
						<Listbox.Option
							key={option.value}
							value={option.value}
							disabled={option.disabled}
							className={({ active, selected }) =>
								classNames(
									'cursor-pointer select-none relative py-2 pl-3 pr-9',
									active ? 'bg-indigo-50' : '',
									selected ? 'bg-indigo-100' : '',
									option.disabled && 'opacity-50 cursor-not-allowed',
								)
							}
						>
							{({ selected }) => (
								<div className="flex items-center">
									{renderOption ? (
										renderOption(option, selected)
									) : (
										<span
											className={classNames(
												'text-xs',
												!option.value ? 'text-gray-500' : 'text-gray-900',
												selected && 'font-semibold',
											)}
										>
											{option.label}
										</span>
									)}
									{selected && (
										<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
									)}
								</div>
							)}
						</Listbox.Option>
					))}

					{allOptions.length === 0 && (
						<div className="px-3 py-2 text-xs text-gray-500 text-center">
							No options available
						</div>
					)}
				</Listbox.Options>
			</div>
		</Listbox>
	);
};

export default InlineSelect;
